import type { AdminPermissionDetail, PageResp } from '@/types';
import type {
  RecordInterfaceLanguageSwitchReq,
  AdminLoginReq,
  AdminLoginResp,
  CountEmployeesResp,
  CountAdminAccountsResp,
  CreateAdminAccountReq,
  CreateAdminAccountResp,
  CreateAdminPostReq,
  CreateAdminPostResp,
  AdminAccountDetail,
  AdminAccountLoginLogsResp,
  AdminAccountOpLogsResp,
  AdminAccountOption,
  DisableAdminAccountResp,
  EnableAdminAccountResp,
  IamSessionResp,
  LoadAdminAccountOptionsReq,
  LoadPostOptionsReq,
  LoadPostOptionsResp,
  PageAdminAccountsReq,
  PageAdminAccountsResp,
  PageEmployeesResp,
  EmployeeContractRow,
  EmployeeEventRow,
  EmployeePage,
  EmploymentAlerts,
  LoadEmployeeProfileResp,
  ExportEmployeesResp,
  CountAppointmentsResp,
  CreateAppointmentReq,
  CreateAppointmentResp,
  LeaveAppointmentReq,
  LeaveAppointmentResp,
  UpdateAppointmentReq,
  UpdateAppointmentResp,
  PagePostsResp,
  ResetAdminAccountPasswordResp,
  SendPasswordResetLinkResp,
  SetPasswordByLinkResp,
} from '@/types/iam-admin';
import { gatewayRequest, type RequestOptions } from '@/utils/http';

/** 记录当前后台帐号的界面语言切换。 */
export const recordInterfaceLanguageSwitch = (data: RecordInterfaceLanguageSwitchReq) =>
  gatewayRequest<void>('/admin/iam/session/recordInterfaceLanguageSwitch', { method: 'POST', data });

// Cookie 在浏览器收到响应时即已生效，store 的版本号无法撤回 Set-Cookie。
// 只串行登录与退出，确保旧身份写响应结束后才发下一次身份变更。
let identityWrite: Promise<void> = Promise.resolve();

/** 按调用顺序完成身份写请求；单次失败仍让后续操作继续。 */
function enqueueIdentityWrite<T>(operation: () => Promise<T>): Promise<T> {
  const result = identityWrite.then(operation);
  identityWrite = result.then(() => undefined, () => undefined);
  return result;
}

/**
 * 后台帐号这一族接口（iam 的 10-03-08 与 10-03-15），路径就是剥掉入口段之后的
 * `<域>/<动作>`，前面拼上 `/admin/iam`（网关按这两段路由到 iam）。
 *
 * **全部走网关**（`gatewayRequest`），因为它们的成功码是 200，与单体那套 /v1/* 不是一回事。
 * 旧单体那一族自 2026-10-08 起在 `utils/http.ts` 的 `request` 出口被拒（不出网），后台这边
 * 的登录、会话、退出、帐号、职员、岗位、密码链接全都在这里。
 */

/** 后台登录（API-10-131）：**后台登录的唯一入口**，登录页只调它这一次，没有降级路径 */
export const iamLogin = (data: AdminLoginReq, options?: RequestOptions) =>
  enqueueIdentityWrite(() => gatewayRequest<AdminLoginResp>('/admin/iam/login', {
    method: 'POST',
    data,
    ...options,
    sessionFailure: 'local',
  }));

/**
 * 当前 iam 会话（API-10-132）：启动 / 刷新后问一次。
 *
 * 它带回「登录了没有」与「能进哪些页、是不是超管」（`reachableRoutes` / `superAdmin`），
 * 所以这是刷新后恢复整份登录态的唯一来源——凭据在 HttpOnly Cookie 里，前端没有本地副本可用。
 */
export const getIamSession = (options?: RequestOptions) =>
  gatewayRequest<IamSessionResp>('/admin/iam/session/getCurrentSession', {
    method: 'POST',
    ...options,
  });

/** 退出 iam 会话（API-10-133）：退出时撤它。浏览器里只剩这一条会话，没有别的要撤 */
export const iamLogout = (options?: RequestOptions) =>
  enqueueIdentityWrite(() => gatewayRequest<unknown>('/admin/iam/logoutAdminSession', {
    method: 'POST',
    data: {},
    ...options,
    sessionFailure: 'local',
  }));

/** 开通后台帐号（API-10-055）：职员帐号与临时帐号共用这一条 */
export const createAdminAccount = (
  data: CreateAdminAccountReq,
  options?: RequestOptions,
) =>
  gatewayRequest<CreateAdminAccountResp>('/admin/iam/admin/createAdminAccount', {
    method: 'POST',
    data,
    ...options,
  });

/** 后台帐号分页（API-10-125）：kw 能按登录邮箱模糊查，超时重发之后靠它核对 */
export const pageAdminAccounts = (
  data: PageAdminAccountsReq,
  options?: RequestOptions,
) =>
  gatewayRequest<PageAdminAccountsResp>('/admin/iam/admin/pageAdminAccounts', {
    method: 'POST',
    data,
    ...options,
  });

/** 后台帐号统计（API-10-126）：列表页拿它判「名额用完没有」，用完就把两颗建号按钮置灰 */
export const countAdminAccounts = (options?: RequestOptions) =>
  gatewayRequest<CountAdminAccountsResp>('/admin/iam/admin/countAdminAccounts', {
    method: 'POST',
    data: {},
    ...options,
  });

/**
 * 修改自己的登录密码（API-10-134）：先验当前密码，改谁由会话定，请求体里没有帐号。
 * 成功后会话令牌不变，本帐号其余设备的登录态被作废。
 */
export const changeLoginPassword = (
  data: { oldPassword: string; newPassword: string; confirmPassword: string },
  options?: RequestOptions,
) =>
  gatewayRequest<{ ok: boolean }>('/admin/iam/password/changeLoginPassword', {
    method: 'POST',
    data,
    ...options,
  });

/** 代重置密码（API-10-130）：给他重发一封设置密码邮件，不发初始密码 */
export const resetAdminAccountPassword = (
  adminId: string,
  options?: RequestOptions,
) =>
  gatewayRequest<ResetAdminAccountPasswordResp>(
    '/admin/iam/admin/password/resetAdminAccountPassword',
    { method: 'POST', data: { adminId }, ...options },
  );

/**
 * 后台帐号详情（API-10-127）：基本信息、权限来源与两类日志的计数（计数可能为 null，表示暂不可判）。
 * 两页签的最近明细不在这一条里，按页签另取（API-10-197 / API-10-198）。
 */
export const detailAdminAccount = (adminId: string, options?: RequestOptions) =>
  gatewayRequest<AdminAccountDetail>('/admin/iam/admin/getAdminAccountDetail', {
    method: 'POST',
    data: { adminId },
    ...options,
  });

/** 后台帐号最近操作日志（API-10-197）：操作日志页签切到时才读，logs 为 null 表示日志库读不到。 */
export const listAdminAccountOpLogs = (
  adminId: string,
  options?: RequestOptions,
) =>
  gatewayRequest<AdminAccountOpLogsResp>(
    '/admin/iam/admin/getAdminAccountOpLogs',
    { method: 'POST', data: { adminId }, ...options },
  );

/** 后台帐号最近登录日志（API-10-198）：登录日志页签切到时才读，logs 为 null 表示日志库读不到。 */
export const listAdminAccountLoginLogs = (
  adminId: string,
  options?: RequestOptions,
) =>
  gatewayRequest<AdminAccountLoginLogsResp>(
    '/admin/iam/admin/getAdminAccountLoginLogs',
    { method: 'POST', data: { adminId }, ...options },
  );

/** 帐号权限明细只在打开时读取，帐号标识遵循 API-10-058 的 accountId。 */
export const loadAdminPermission = (accountId: string, options?: RequestOptions) =>
  gatewayRequest<AdminPermissionDetail>('/admin/iam/admin/permission/loadAdminPermission', {
    method: 'POST', data: { accountId }, ...options,
  });

/** 停用后台帐号（API-10-090）：原型列表行操作，版本号由列表响应提供。 */
export const disableAdminAccount = (
  adminId: string,
  version: number,
  reason?: string,
  options?: RequestOptions,
) =>
  gatewayRequest<DisableAdminAccountResp>('/admin/iam/admin/disableAdminAccount', {
    method: 'POST',
    data: { adminId, version, reason },
    ...options,
  });

/** 启用后台帐号（API-10-091）：与停用分开建模，避免丢失乐观锁版本。 */
export const enableAdminAccount = (
  adminId: string,
  version: number,
  options?: RequestOptions,
) =>
  gatewayRequest<EnableAdminAccountResp>('/admin/iam/admin/enableAdminAccount', {
    method: 'POST',
    data: { adminId, version },
    ...options,
  });

/** 职员分页（API-10-081）：开职员帐号那两页的列表数据 */
export const pageEmployees = (data: Record<string, unknown>, options?: RequestOptions) =>
  gatewayRequest<PageEmployeesResp>('/admin/iam/employee/pageEmployees', {
    method: 'POST',
    data,
    ...options,
  });

export const countEmployees = (data: Record<string, unknown> = {}, options?: RequestOptions) =>
  gatewayRequest<CountEmployeesResp>('/admin/iam/employee/countEmployees', { method: 'POST', data, ...options });

export const loadEmploymentAlerts = (options?: RequestOptions) =>
  gatewayRequest<EmploymentAlerts>('/admin/iam/employee/loadEmploymentAlerts', { method: 'POST', data: {}, ...options });

export const loadEmployeeProfile = (employeeNo: string, options?: RequestOptions) =>
  gatewayRequest<LoadEmployeeProfileResp>('/admin/iam/employee/loadEmployeeProfile', { method: 'POST', data: { employeeNo }, ...options });

export const pageEmployeeContracts = (employeeNo: string, options?: RequestOptions) =>
  gatewayRequest<EmployeePage<EmployeeContractRow>>('/admin/iam/employee/contract/pageEmployeeContracts', { method: 'POST', data: { employeeNo, page: 1, size: 100 }, ...options });

export const pageEmployeeEvents = (employeeNo: string, options?: RequestOptions) =>
  gatewayRequest<EmployeePage<EmployeeEventRow>>('/admin/iam/employee/event/pageEmployeeEvents', { method: 'POST', data: { employeeNo, page: 1, size: 100 }, ...options });

export const exportEmployees = (data: Record<string, unknown>, options?: RequestOptions) =>
  gatewayRequest<ExportEmployeesResp>('/admin/iam/employee/exportEmployees', { method: 'POST', data, ...options });

/** 入职与新增任职（API-10-085）：新建档案与「选已有员工档案」是同一条，靠 source 分流 */
export const createAppointment = (data: CreateAppointmentReq, options?: RequestOptions) =>
  gatewayRequest<CreateAppointmentResp>('/admin/iam/appointment/createAppointment', { method: 'POST', data, ...options });

/**
 * 变更职位与编辑合同（API-10-086）：`version` 是打开这一段任职时读到的乐观锁，
 * 对不上按冲突拒、不覆盖；改完职位回的是新的权限岗位名与下一次要用的版本号。
 */
export const updateAppointment = (data: UpdateAppointmentReq, options?: RequestOptions) =>
  gatewayRequest<UpdateAppointmentResp>('/admin/iam/appointment/updateAppointment', { method: 'POST', data, ...options });

/**
 * 办理离职（API-10-087）：结束这一段任职并当场收回它带来的权限。
 * 后台帐号按需求不自动停用，固定传 `disableAdmin=false` 并给 `keepReason`；`revokeAdminPosts` 照常收回临时岗位。
 */
export const leaveAppointment = (data: LeaveAppointmentReq, options?: RequestOptions) =>
  gatewayRequest<LeaveAppointmentResp>('/admin/iam/appointment/leaveAppointment', { method: 'POST', data, ...options });

/** 部门各职位在任人数（API-10-044）：入职抽屉的职位下拉与编制提示共用这一条，只列启用中的职位 */
export const countAppointments = (
  data: { deptId?: string | null; titleIds?: string[] | null; activeOnly?: boolean } = {},
  options?: RequestOptions,
) =>
  gatewayRequest<CountAppointmentsResp>('/admin/iam/appointment/countAppointments', { method: 'POST', data, ...options });

/** 岗位分页（API-10-070）：临时帐号建号时选岗位用 */
export const pagePosts = (data: Record<string, unknown>, options?: RequestOptions) =>
  gatewayRequest<PagePostsResp>('/admin/iam/post/pagePosts', {
    method: 'POST',
    data,
    ...options,
  });

/**
 * 可授予帐号选项源（API-10-062，`10-03-14-11`）：六个入口共用一份实现，权限词按入口分开校验。
 *
 * 日志筛选区（操作日志 / 登录日志 / 人员审计）用的是 `scope=all` 那一档——本租户全部
 * 后台帐号（含机构主体与已停用），因为「日志里有它做的事，筛选区选不到它就查不到」；
 * 过滤与分页都在服务端（`kw` 模糊、`size` 有上限），不许发全量让前端筛。
 *
 * **「系统」不是帐号**：`operator_id` 为空的那一组由前端加一个常量选项（虚拟值 `system`），
 * 本接口不返回一个叫「系统」的假帐号。
 */
export const loadAdminAccountOptions = (
  data: LoadAdminAccountOptionsReq,
  options?: RequestOptions,
) =>
  gatewayRequest<PageResp<AdminAccountOption>>(
    '/admin/iam/admin/loadAdminAccountOptions',
    { method: 'POST', data, ...options },
  );

/**
 * 可授予岗位选项源（API-10-063）：五个页面共用一份实现。
 * 不是分页接口，响应就是 `rows[]`；加临时岗位抽屉传 excludeBuiltin / status='normal'。
 */
export const loadPostOptions = (
  data: LoadPostOptionsReq,
  options?: RequestOptions,
) =>
  gatewayRequest<LoadPostOptionsResp>('/admin/iam/post/loadPostOptions', {
    method: 'POST',
    data,
    ...options,
  });

/**
 * 加临时岗位（API-10-060）：逐个岗位写授予行，已有有效授予的跳过。
 * to 与 reason 是接口层必填；授予成功不写投影行，到点由定时扫描推动生效与失效。
 */
export const createAdminPost = (
  data: CreateAdminPostReq,
  options?: RequestOptions,
) =>
  gatewayRequest<CreateAdminPostResp>('/admin/iam/adminPost/createAdminPost', {
    method: 'POST',
    data,
    ...options,
  });

/**
 * 忘记密码发重置链接（API-10-135）：回执与「有没有这个帐号」无关。
 * `tenantCode` 是登录表单里的机构编码，单域名后台上网关按它定租户。
 */
export const sendPasswordResetLink = (
  loginId: string,
  tenantCode: string,
  options?: RequestOptions,
) =>
  gatewayRequest<SendPasswordResetLinkResp>(
    '/admin/iam/password/sendPasswordResetLink',
    { method: 'POST', data: { loginId, tenantCode }, ...options, sessionFailure: 'local' },
  );

/**
 * 凭一次性链接设置后台密码（API-10-148）：免登录页提交的就是它。
 * `tenantCode` 取自链接查询参数，单域名后台上网关按它定租户；链接没带就不传。
 */
export const setPasswordByLink = (
  data: { token: string; password: string; tenantCode?: string },
  options?: RequestOptions,
) =>
  gatewayRequest<SetPasswordByLinkResp>('/admin/iam/password/setPasswordByLink', {
    method: 'POST',
    data,
    ...options,
    sessionFailure: 'local',
  });
